/**
 * SaleDetail — Full-page wrapper that loads a sale by ID and renders it
 * inside the shared UnifiedInvoiceModal. Replaces the legacy 1400-line
 * invoice page so every entry-point (/sales/:id, /invoices/:id, POS
 * "View Invoice") shows the same modern design.
 */
import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import UnifiedInvoiceModal from "../components/UnifiedInvoiceModal";
import { Toaster } from "../components/ui/sonner";
import { api, saleRefundRows, money, loadSaleInvoiceExtras } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function SaleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: fullSale } = await api.get(`/sales/${id}`);

      // Linked online invoice (richer data) + warranty, fetched together.
      const { linkedInvoice, warranty } = await loadSaleInvoiceExtras(fullSale);

      if (linkedInvoice) {
        linkedInvoice.invoice_number = fullSale.sale_number || linkedInvoice.invoice_number;
        // Void / delete / payment actions must target the authoritative sale.
        linkedInvoice.backing_sale_id = fullSale.id;
        setInvoice(linkedInvoice);
      } else {
        // Build UnifiedInvoiceModal-compatible object from the sale record
        const payments = (fullSale.payments || []).map((p, i) => ({
          payment_id: p.id || null,
          payment_index: i,
          method: p.method || "cash",
          amount: p.amount || 0,
          paid_at: p.paid_at || p.at || fullSale.created_at,
          cashier: p.cashier || p.cashier_name || fullSale.cashier_name || "Staff",
          is_refund: p.is_refund || false,
          note: p.note || "",
          tyro_meta: p.tyro_meta || null,
          corrected_at: p.corrected_at || null,
          correction_reason: p.correction_reason || "",
          tyro_offline: !!p.tyro_offline,
          tyro_auth_code: p.tyro_auth_code || "",
          edited_at: p.edited_at || null,
          edit_reason: p.edit_reason || "",
          edited_by_name: p.edited_by_name || "",
        }));
        const totalPaid = payments.reduce((s, p) => s + (p.is_refund || p.corrected_at ? 0 : p.amount), 0);
        const amountDue = Math.max(0, parseFloat(fullSale.total || 0) - totalPaid);

        setInvoice({
          id: fullSale.id,
          invoice_number: fullSale.sale_number,
          customer_id: fullSale.customer_id || null,
          customer_name: fullSale.customer_name || "Walk-in",
          customer_phone: fullSale.customer_phone || "",
          customer_email: fullSale.customer_email || "",
          total: fullSale.total,
          discount: fullSale.discount || 0,
          store_credit_redeemed: fullSale.store_credit_redeemed || 0,
          amount_due: amountDue,
          in_store_paid: totalPaid,
          status: fullSale.voided ? "voided" : amountDue <= 0.005 ? "paid" : "draft",
          items: (fullSale.items || []).map((it) => ({
            description: it.name || it.description || "Item",
            quantity: it.quantity || 1,
            unit_price: it.unit_price || it.price || 0,
            device_label: it.device_label || null,
          })),
          payment_history: [...payments, ...saleRefundRows(fullSale)],
          devices: warranty.devices, accessories_warranty: warranty.accessories_warranty, item_warranties: warranty.item_warranties,
          created_at: fullSale.created_at,
          linked_ticket_id: (fullSale.items || []).find((it) => it.repair_ticket_id)?.repair_ticket_id || null,
          _is_sale: true,
        });
      }
    } catch {
      setInvoice(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-[14px] text-[#9CA3AF] font-semibold tracking-wider animate-pulse">Loading invoice...</div>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center h-[60vh] gap-4">
          <div className="text-[16px] text-[#9CA3AF] font-semibold">Invoice not found</div>
          <button
            onClick={() => nav("/invoices")}
            className="h-10 px-5 rounded-lg border border-[#E5E7EB] text-[13px] font-semibold hover:border-[#121216] transition-colors"
            data-testid="back-to-invoices"
          >
            Back to Invoices
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <>
      <UnifiedInvoiceModal
        invoice={invoice}
        onClose={() => nav(-1)}
        onUpdated={load}
        user={user}
      />
      <Toaster position="top-right" richColors />
    </>
  );
}
